import axios from 'axios'
import type { AxiosInstance, AxiosResponse } from 'axios'

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8788'

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
  meta?: any
}

class ApiClient {
  private client: AxiosInstance
  private apiKey: string | null = null

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.client.interceptors.request.use(
      (config) => {
        if (this.apiKey) {
          config.headers.XAPIKey = this.apiKey
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized - redirect to login
          window.location.href = '/'
        }
        return Promise.reject(error)
      }
    )
  }

  setApiKey(apiKey: string) {
    this.apiKey = apiKey
    localStorage.setItem('agent_api_key', apiKey)
  }

  getApiKey(): string | null {
    if (!this.apiKey) {
      this.apiKey = localStorage.getItem('agent_api_key')
    }
    return this.apiKey
  }

  clearApiKey() {
    this.apiKey = null
    localStorage.removeItem('agent_api_key')
  }

  async get<T>(url: string, params?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<T> = await this.client.get(url, { params })
    return response.data
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<T> = await this.client.post(url, data)
    return response.data
  }

  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<T> = await this.client.patch(url, data)
    return response.data
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    const response: AxiosResponse<T> = await this.client.delete(url)
    return response.data
  }
}

export const apiClient = new ApiClient()
